import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CustomDropdown,
  CustomDropdownWithFilter,
  InputCep,
  InputPhone,
  InputText,
  SectionTitle,
} from '../../../../components'
import InputCnpjCpf from '../../../../components/InputCnpjCpf/InputCnpjCpf'
import { CTE_TIPOS_PESSOA } from '../../../../constants/cte-sem-nota'
import { NFSE_TOMADOR_ORIGENS } from '../../../../constants/fiscal'
import { getTomadorNfse } from '../../../../services/api/https/fiscal/nfse.http'
import { getCnpjDataFocusNfe } from '../../../../services/api/https/system/system.http'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { somenteDigitos } from '../../../../services/util/documento.service'
import RadioGroup from '../../Entrega/Cte/Partes/RadioGroup'
import SelectClienteTp from '../../Entrega/Cte/Partes/SelectClienteTp'
import SelectOperadorLogistico from '../../Entrega/Cte/Partes/SelectOperadorLogistico'
import { tomadorDoCadastro, tomadorVazio } from './emitirNfse.util'
import './EmitirNfse.css'

const estiloDica = { color: 'gray', fontSize: 12 }

// Origem Empresa/Cliente/Operador Logístico pré-preenche pelo cadastro; Manual busca o CNPJ na Receita.
// Tudo fica editável e vale só para esta NFS-e (não altera o cadastro).
function TomadorNfseForm({ tomador, onChange, ufs, carregarCidades, tpId, onError }) {
  const [cidades, setCidades] = useState([])
  const [carregando, setCarregando] = useState(false)
  const tomadorRef = useRef(tomador)
  tomadorRef.current = tomador

  const empresas = useMemo(() => getEmpresas(), [])
  // A empresa das notas pode não estar entre as empresas do usuário: entra como opção para aparecer no campo
  const opcoesEmpresas = useMemo(() => {
    if (!tomador.company_id || empresas.some(empresa => empresa.id === tomador.company_id))
      return empresas
    return [...empresas, { id: tomador.company_id, name: tomador.razao_social }]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresas, tomador.company_id])
  const empresaSelecionada =
    opcoesEmpresas.find(empresa => empresa.id === tomador.company_id) || null
  const clienteSelecionado = tomador.client_id
    ? { id: tomador.client_id, rotulo: `${tomador.razao_social} - ${tomador.cnpj_cpf}` }
    : null
  const ufId = tomador.uf ? tomador.uf.id : null

  // Cidades da UF; se a cidade pré-preenchida não existir no cadastro, exige seleção manual
  useEffect(() => {
    let ativo = true
    if (!ufId) {
      setCidades([])
      return undefined
    }
    carregarCidades(ufId)
      .then(lista => {
        if (!ativo) return
        setCidades(lista)
        const atual = tomadorRef.current.city
        if (atual && !lista.some(cidade => cidade.id === atual.id)) {
          onChange({ ...tomadorRef.current, city: null })
          onError(
            new Error(
              'Tomador: cidade não encontrada no cadastro de cidades, selecione manualmente',
            ),
          )
        }
      })
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufId])

  // Helpers
  const atualizar = patch => onChange({ ...tomadorRef.current, ...patch })

  const carregarCadastro = async (origem, id) => {
    try {
      setCarregando(true)
      const dados = await getTomadorNfse(tpId, origem, id)
      onChange(tomadorDoCadastro(dados, ufs))
    } catch (error) {
      onError(error)
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    if (tomador.origem === 'tp' && !tomador.tp_id && tpId) carregarCadastro('tp', tpId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tomador.origem])

  const buscarCnpj = async cnpj => {
    try {
      setCarregando(true)
      const dados = await getCnpjDataFocusNfe(cnpj)
      const endereco = dados.endereco || {}
      const cidadeId = parseInt(endereco.codigo_ibge, 10)
      atualizar({
        razao_social: String(dados.razao_social || '').slice(0, 150),
        logradouro: endereco.logradouro || '',
        numero: endereco.numero || '',
        complemento: String(endereco.complemento || '').slice(0, 156),
        bairro: endereco.bairro || '',
        cep: somenteDigitos(endereco.cep),
        uf: ufs.find(item => item.uf === endereco.uf) || null,
        city: cidadeId ? { id: cidadeId, name: endereco.nome_municipio || '' } : null,
      })
    } catch (error) {
      onError(error)
    } finally {
      setCarregando(false)
    }
  }

  // Events
  const onDocumentoChange = ({ value }) => {
    atualizar({ cnpj_cpf: value })
    if (tomador.origem === 'manual' && tomador.tipo_pessoa === 'PJ' && value.length === 14) {
      buscarCnpj(value)
    }
  }

  // Render
  return (
    <>
      <div className="p-col-12">
        <SectionTitle title="Tomador do serviço" />
        <div style={{ marginTop: 5 }}>
          <RadioGroup
            nome="nfse-tomador-origem"
            items={NFSE_TOMADOR_ORIGENS}
            value={tomador.origem}
            onChange={origem => onChange(tomadorVazio(origem))}
          />
        </div>
      </div>

      {tomador.origem === 'empresa' && (
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="name"
            options={opcoesEmpresas}
            value={empresaSelecionada}
            label="Empresa:*"
            filter={true}
            disabled={carregando}
            onChange={empresa => carregarCadastro('empresa', empresa.id)}
          />
        </div>
      )}

      {tomador.origem === 'cliente' && (
        <div className="p-md-6 p-col-12">
          <SelectClienteTp
            selectedTp={tpId}
            selecionado={clienteSelecionado}
            disabled={carregando}
            onSelect={cliente => carregarCadastro('cliente', cliente.id)}
            onError={onError}
          />
        </div>
      )}

      {tomador.origem === 'tp' && (
        <div className="p-md-6 p-col-12">
          <SelectOperadorLogistico
            value={tomador.tp_id}
            disabled={carregando}
            onChange={tp => carregarCadastro('tp', tp.id)}
          />
        </div>
      )}

      <div className="p-md-4 p-col-12" style={{ marginTop: 10 }}>
        <label style={{ fontSize: '0.8rem', color: '#14B0BF', display: 'block', marginBottom: 6 }}>
          Tipo de pessoa:*
        </label>
        <RadioGroup
          nome="nfse-tomador-tipo-pessoa"
          items={CTE_TIPOS_PESSOA}
          value={tomador.tipo_pessoa}
          onChange={tipoPessoa => atualizar({ tipo_pessoa: tipoPessoa, cnpj_cpf: '' })}
        />
      </div>
      <div className="p-md-3 p-col-12">
        <InputCnpjCpf
          label={tomador.tipo_pessoa === 'PF' ? 'CPF:*' : 'CNPJ:*'}
          marginTop={10}
          value={tomador.cnpj_cpf}
          onChange={onDocumentoChange}
        />
        {tomador.origem === 'manual' && tomador.tipo_pessoa === 'PJ' && (
          <span style={estiloDica}>
            Ao completar o CNPJ, nome e endereço são buscados na Receita
          </span>
        )}
      </div>
      <div className="p-md-5 p-col-12">
        <InputText
          label="Razão social / Nome:*"
          marginTop={10}
          value={tomador.razao_social}
          maxLength={150}
          onChange={razao_social => atualizar({ razao_social })}
        />
      </div>

      <div className="p-md-3 p-col-12">
        <InputText
          label="Inscrição Municipal:"
          marginTop={10}
          value={tomador.inscricao_municipal}
          maxLength={15}
          onChange={inscricao_municipal => atualizar({ inscricao_municipal })}
        />
      </div>
      <div className="p-md-5 p-col-12">
        <InputText
          label="E-mail:"
          marginTop={10}
          value={tomador.email}
          maxLength={80}
          onChange={email => atualizar({ email })}
        />
        <span style={estiloDica}>Em produção, a NFS-e é enviada para este e-mail</span>
      </div>
      <div className="p-md-4 p-col-12">
        <InputPhone
          label="Telefone:"
          marginTop={10}
          value={tomador.telefone}
          onChange={telefone => atualizar({ telefone: telefone || '' })}
        />
      </div>

      <div className="p-md-6 p-col-12">
        <InputText
          label="Logradouro:*"
          marginTop={10}
          value={tomador.logradouro}
          maxLength={255}
          onChange={logradouro => atualizar({ logradouro })}
        />
      </div>
      <div className="p-md-2 p-col-12">
        <InputText
          label="Número:*"
          marginTop={10}
          value={tomador.numero}
          maxLength={60}
          onChange={numero => atualizar({ numero })}
        />
      </div>
      <div className="p-md-4 p-col-12">
        <InputText
          label="Complemento:"
          marginTop={10}
          value={tomador.complemento}
          maxLength={156}
          onChange={complemento => atualizar({ complemento })}
        />
      </div>
      <div className="p-md-4 p-col-12">
        <InputText
          label="Bairro:*"
          marginTop={10}
          value={tomador.bairro}
          maxLength={60}
          onChange={bairro => atualizar({ bairro })}
        />
      </div>
      <div className="p-md-2 p-col-12 EmitirNfse-uf">
        <CustomDropdown
          optionLabel="uf"
          options={ufs}
          value={tomador.uf}
          label="UF:*"
          filter={true}
          onChange={uf => atualizar({ uf, city: null })}
        />
      </div>
      <div className="p-md-3 p-col-12">
        <CustomDropdownWithFilter
          optionLabel="name"
          options={cidades}
          value={tomador.city}
          label="Cidade:*"
          filter={true}
          disabled={!tomador.uf}
          onChange={city => atualizar({ city })}
        />
      </div>
      <div className="p-md-3 p-col-12">
        <InputCep
          label="CEP:*"
          marginTop={10}
          value={tomador.cep}
          onChange={values => atualizar({ cep: values.value })}
        />
      </div>
      <div className="p-col-12" style={{ paddingTop: 0 }}>
        <span style={estiloDica}>
          Alterações feitas aqui valem só para esta NFS-e e não atualizam o cadastro.
        </span>
      </div>
    </>
  )
}

export default React.memo(TomadorNfseForm)
